type WalletEntry =
  | { type: "event"; startDateTime: string }
  | { type: "merch"; orders?: readonly { orderedAt: string }[] }
  | { type: "music" };

/**
 * Where an entry sits in the wallet: an event at its start, a merch order
 * at the date it was ordered (its newest order's). Null for what has
 * neither (owned music).
 */
export function walletDate(entry: WalletEntry): number | null {
  const at =
    entry.type === "event"
      ? entry.startDateTime
      : entry.type === "merch"
        ? entry.orders?.reduce<string | null>(
            (newest, order) =>
              newest === null || order.orderedAt > newest
                ? order.orderedAt
                : newest,
            null,
          )
        : null;
  const time = at ? Date.parse(at) : NaN;
  return Number.isNaN(time) ? null : time;
}

/**
 * The wallet's order, the API's and the app's: newest first by
 * `walletDate`, so an upcoming event comes before a merch order placed
 * today and that before past events; entries without a date follow, as
 * they came. Returns a new array.
 */
export function sortWallet<T extends WalletEntry>(entries: readonly T[]): T[] {
  return entries
    .map((entry, index) => ({ entry, index, at: walletDate(entry) }))
    .sort((a, b) =>
      a.at !== null && b.at !== null
        ? b.at - a.at || a.index - b.index
        : a.at !== null
          ? -1
          : b.at !== null
            ? 1
            : a.index - b.index,
    )
    .map(({ entry }) => entry);
}
